/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice } from '@reduxjs/toolkit';
import { AugmentedUser } from 'types/avatarSheet';

export interface TeamActivityState {
  loading: boolean;
  isRevalidating: boolean;
  error: boolean;
  users: AugmentedUser[];
  hasEverFetched: boolean;
  currentUserData: any | null;
  currentUserLoading: boolean;
}

const initialState: TeamActivityState = {
  loading: false,
  isRevalidating: false,
  error: false,
  users: [],
  hasEverFetched: false,
  currentUserData: null,
  currentUserLoading: false,
};

export const teamActivitySlice = createSlice({
  name: 'teamActivity',
  initialState,
  reducers: {
    fetchTeamActivity: () => {},
    setTeamActivityLoading: (state) => ({
      ...state,
      loading: true,
      error: false,
      currentUserLoading: true,
    }),
    setTeamActivityRevalidating: (state) => ({
      ...state,
      isRevalidating: true,
      error: false,
    }),
    setTeamActivitySuccess: (state, action) => ({
      ...state,
      loading: false,
      isRevalidating: false,
      users: action.payload.users,
      currentUserData: action.payload.currentUserData,
      hasEverFetched: true,
      currentUserLoading: false,
    }),
    setTeamActivityError: (state) => ({
      ...state,
      loading: false,
      error: true,
      currentUserLoading: false,
    }),
    fetchAndUpdateUserActiveLog: (_state, _action: { payload: { userId: string } }) => {},
    setUserActiveLog: (state, action) => {
      const { userId, activeLog } = action.payload;

      const userIndex = state.users.findIndex((u) => u.uuid === userId);
      if (userIndex !== -1) {
        state.users[userIndex].activeLog = activeLog;
      }

      if (state.currentUserData && state.currentUserData.uuid === userId) {
        state.currentUserData.activeLog = activeLog;
      }
    },
    setUserTimelogs: (state, action) => {
      const { userId, activeLog, pausedLogs } = action.payload;

      const userIndex = state.users.findIndex((u) => u.uuid === userId);
      if (userIndex !== -1) {
        state.users[userIndex].activeLog = activeLog;
        state.users[userIndex].pausedLogs = pausedLogs;
      }

      if (state.currentUserData && state.currentUserData.uuid === userId) {
        state.currentUserData.activeLog = activeLog;
        state.currentUserData.pausedLogs = pausedLogs;
      }
    },
    applyTimelogUpdateFromWebSocket: (state, action) => {
      const { logId, userId, newStatus, updatedLog } = action.payload;

      const update = (user: any) => {
        if (newStatus === 'running') {
          const log = updatedLog || user.pausedLogs?.find((l: any) => l.uuid === logId);
          if (log) {
            user.activeLog = {
              ...log,
              status: 'running',
              started_at: updatedLog?.started_at ?? new Date().toISOString(),
            };
            user.pausedLogs = user.pausedLogs?.filter((l: any) => l.uuid !== logId);
          }
        } else if (newStatus === 'paused') {
          if (user.activeLog?.uuid === logId) {
            const log = updatedLog || user.activeLog;
            user.pausedLogs = [...(user.pausedLogs || []), { ...log, status: 'paused' }];
            user.activeLog = null;
          }
        }
      };

      const userIndex = state.users.findIndex((u) => u.uuid === userId);
      if (userIndex !== -1) update(state.users[userIndex]);
      if (state.currentUserData?.uuid === userId) update(state.currentUserData);
    },
    updateUserTimelogFromWebSocket: (state, action) => {
      const { userId } = action.payload;

      const userIndex = state.users.findIndex((u) => u.uuid === userId);
      if (userIndex !== -1) {
        state.users[userIndex].activeLog = null;
      }

      if (state.currentUserData && state.currentUserData.uuid === userId) {
        state.currentUserData.activeLog = null;
      }
    },
    updateUserStatusFromWebSocket: (state, action) => {
      const { userId, activityStatus } = action.payload;

      const userIndex = state.users.findIndex((u) => u.uuid === userId);
      if (userIndex !== -1) {
        state.users[userIndex].activity_status = activityStatus;
        state.users[userIndex].resolvedStatus = activityStatus;
      }

      // Also update current user if it matches
      if (state.currentUserData && state.currentUserData.uuid === userId) {
        state.currentUserData.activity_status = activityStatus;
        state.currentUserData.resolvedStatus = activityStatus;
      }
    },
    resetTeamActivity: () => initialState,
  },
});

export const {
  fetchTeamActivity,
  setTeamActivityLoading,
  setTeamActivityRevalidating,
  setTeamActivitySuccess,
  setTeamActivityError,
  fetchAndUpdateUserActiveLog,
  setUserActiveLog,
  setUserTimelogs,
  applyTimelogUpdateFromWebSocket,
  updateUserTimelogFromWebSocket,
  updateUserStatusFromWebSocket,
  resetTeamActivity,
} = teamActivitySlice.actions;

export default teamActivitySlice.reducer;
